import './index.scss'
import usePlaylists from '@hooks/usePlaylists';
import Playlist from '@data/interfaces/playlist.interface';
import { useChurchDataProvider } from '@data/ChurchDataProvider';
import LoadingIndicator from '@components/LoadingIndicator';
import ErrorView from '@components/ErrorView';
import AddPlaylistModal from '@pages/v2/Playlists/Modal/AddPlaylistModal';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import plusPlain from 'url:@assets/icons/plus.plain.svg'
import songPlaceholder from 'url:@assets/images/SongPlaceholder.png'
import PageTabs from '@components/PageTabs';

export default function Playlists() {
  const navigate = useNavigate();
  const churchData = useChurchDataProvider();
  const { playlists, loading, error, refetch, createPlaylist } = usePlaylists();
  const [isAddPlaylistModalOpen, setIsAddPlaylistModalOpen] = useState(false);
  const groupedPlaylists = groupPlaylists(playlists || [], churchData.campuses, churchData.categories);

  return <div className="playlists">
    <AddPlaylistModal
      isOpen={isAddPlaylistModalOpen}
      setIsOpen={setIsAddPlaylistModalOpen}
      onSave={createPlaylist}
    />

    <PageTabs title="Playlists"
      tabs={[]}
      buttons={[
        {
          icon: 'add',
          label: 'Create Playlist',
          onClick: () => {
            setIsAddPlaylistModalOpen(true)
          }
        }
      ]} />

    {loading ? <LoadingIndicator fullScreen={true} /> : null}
    {error ? <ErrorView error={error} buttonText="Retry" buttonAction={() => refetch()} /> : null}

    {groupedPlaylists.map((campusGroup) => (
      <section key={campusGroup.id} className='playlist-campus-group'>
        <div className='playlist-group-heading'>
          <h2>{campusGroup.title}</h2>
        </div>

        {campusGroup.categories.map((categoryGroup) => (
          <div key={categoryGroup.id} className='playlist-category-group'>
            <div className='playlist-group-subheading'>
              <h3>{categoryGroup.title}</h3>
            </div>

            <div className="playlists-list">
              {categoryGroup.playlists.map(playlist => {
                return <button
                  key={playlist.id}
                  type='button'
                  className='playlist'
                  onClick={() => {
                    navigate(`/playlists/${playlist.id}`, {
                      state: {
                        playlist
                      }
                    });
                  }}
                >
                  <PlaylistDetail playlist={playlist} />
                  <div className='playlist-info'>
                    <h2 className='title'>{playlist.attributes.name}</h2>
                    <p className='subtitle'>{playlist.songs.length} song{playlist.songs.length !== 1 ? 's' : ''}</p>
                  </div>
                </button>
              })}
            </div>
          </div>
        ))}
      </section>
    ))}
  </div>
}

type CampusOption = {
  id: string;
  attributes: {
    name: string;
  };
};

type CategoryOption = {
  id: string;
  name: string;
};

function groupPlaylists(
  playlists: Playlist[],
  campuses: CampusOption[],
  categories: CategoryOption[]
) {
  const campusNameByID = new Map(campuses.map((campus) => [campus.id, campus.attributes.name]));
  const categoryNameByID = new Map(categories.map((category) => [category.id, category.name]));
  const sortedPlaylists = [...playlists].sort((a, b) => {
    const campusA = getCampusLabel(a, campusNameByID);
    const campusB = getCampusLabel(b, campusNameByID);
    if (campusA !== campusB) {
      return campusA.localeCompare(campusB);
    }

    const categoryA = getCategoryLabel(a, categoryNameByID);
    const categoryB = getCategoryLabel(b, categoryNameByID);
    if (categoryA !== categoryB) {
      return categoryA.localeCompare(categoryB);
    }

    return a.attributes.name.localeCompare(b.attributes.name);
  });

  const campusGroups = new Map<string, {
    id: string;
    title: string;
    categories: Map<string, {
      id: string;
      title: string;
      playlists: Playlist[];
    }>;
  }>();

  sortedPlaylists.forEach((playlist) => {
    const campusID = playlist.attributes.campusID || 'no-campus';
    const campusTitle = getCampusLabel(playlist, campusNameByID);
    const categoryID = playlist.attributes.categoryID || 'no-category';
    const categoryTitle = getCategoryLabel(playlist, categoryNameByID);

    if (!campusGroups.has(campusID)) {
      campusGroups.set(campusID, {
        id: campusID,
        title: campusTitle,
        categories: new Map()
      });
    }

    const campusGroup = campusGroups.get(campusID)!;
    if (!campusGroup.categories.has(categoryID)) {
      campusGroup.categories.set(categoryID, {
        id: categoryID,
        title: categoryTitle,
        playlists: []
      });
    }

    campusGroup.categories.get(categoryID)!.playlists.push(playlist);
  });

  return Array.from(campusGroups.values()).map((campusGroup) => ({
    id: campusGroup.id,
    title: campusGroup.title,
    categories: Array.from(campusGroup.categories.values())
  }));
}

function getCampusLabel(playlist: Playlist, campusNameByID: Map<string, string>) {
  if (!playlist.attributes.campusID) {
    return 'No Campus';
  }

  return campusNameByID.get(playlist.attributes.campusID) || 'Unknown Campus';
}

function getCategoryLabel(playlist: Playlist, categoryNameByID: Map<string, string>) {
  if (!playlist.attributes.categoryID) {
    return 'No Category';
  }

  return categoryNameByID.get(playlist.attributes.categoryID) || 'Unknown Category';
}

const PlaylistDetail = (props: {
  playlist: Playlist
}) => {
  const thumbnails = props.playlist.songs.slice(0, 4);
  const collageItems = thumbnails.length > 0
    ? thumbnails.map((song, index) => ({
      id: `${song.id}-${index}`,
      src: song.attributes.artworkURL || songPlaceholder
    }))
    : [{
      id: 'placeholder',
      src: songPlaceholder
    }];
  const collageCount = Math.min(Math.max(collageItems.length, 1), 4);

  return <div className='playlist-detail'>
    <div className={`playlist-collage count-${collageCount} ${thumbnails.length === 0 ? 'is-placeholder' : ''}`} aria-hidden="true">
      {collageItems.map((item, index) => (
        <img key={item.id} src={item.src} alt='' className={`cover-art tile-${index + 1}`} />
      ))}
    </div>
  </div>
}
